import type { Review } from '@shopgnd/shared';
import { resolveCatalogAuth } from '@/lib/amplify-client';
import { getIdToken } from '@/lib/auth';
import type { ReviewDisplay } from '@/lib/reviews';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyDataClient = any;

function toDisplay(review: Review): ReviewDisplay {
  return {
    ...review,
    authorDisplayName: 'ShopGND customer',
    authorParish: undefined,
  };
}

async function reviewAggregateUrl(): Promise<string | null> {
  const fromEnv = import.meta.env.VITE_REVIEW_AGGREGATE_URL;
  if (fromEnv) return fromEnv;
  try {
    const cacheBust = import.meta.env.DEV ? `?t=${Date.now()}` : '';
    const res = await fetch(`/amplify_outputs.json${cacheBust}`, { cache: 'no-store' });
    if (res.ok) {
      const outputs = (await res.json()) as { custom?: { reviewAggregateUrl?: string } };
      return outputs.custom?.reviewAggregateUrl ?? null;
    }
  } catch {
    /* missing outputs */
  }
  return null;
}

async function triggerReviewAggregate(targetType: Review['targetType'], targetId: string): Promise<void> {
  if (targetType === 'product') return;
  const url = await reviewAggregateUrl();
  if (!url) return;
  const token = await getIdToken();
  if (!token) return;
  try {
    await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ targetType, targetId }),
    });
  } catch {
    /* non-fatal */
  }
}

export async function listReviews(opts: {
  targetType: Review['targetType'];
  targetId: string;
}): Promise<ReviewDisplay[]> {
  try {
    const { generateClient } = await import('aws-amplify/data');
    const client = generateClient() as AnyDataClient;
    const res = await client.models.Review.list({
      ...(await resolveCatalogAuth()),
      filter: {
        targetType: { eq: opts.targetType },
        targetId: { eq: opts.targetId },
      },
    });
    const items = (res.data ?? []) as unknown as Review[];
    return items.map(toDisplay).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  } catch (e) {
    console.error('[ShopGND] listReviews failed', e);
    return [];
  }
}

export async function createReview(input: {
  authorUserId: string;
  targetType: Review['targetType'];
  targetId: string;
  rating: number;
  comment?: string;
  orderId?: string;
}): Promise<ReviewDisplay> {
  const review: Review = {
    id: `rev-${Date.now()}`,
    authorUserId: input.authorUserId,
    targetType: input.targetType,
    targetId: input.targetId,
    rating: input.rating,
    comment: input.comment?.trim() || undefined,
    orderId: input.orderId,
    createdAt: new Date().toISOString(),
  };

  const { generateClient } = await import('aws-amplify/data');
  const client = generateClient() as AnyDataClient;
  const res = await client.models.Review.create(review);
  const saved = (res.data ?? review) as unknown as Review;
  void triggerReviewAggregate(input.targetType, input.targetId);
  return toDisplay(saved);
}

export async function replyToReview(reviewId: string, vendorReply: string): Promise<ReviewDisplay | null> {
  const { generateClient } = await import('aws-amplify/data');
  const client = generateClient() as AnyDataClient;
  const res = await client.models.Review.update({ id: reviewId, vendorReply: vendorReply.trim() });
  return res.data ? toDisplay(res.data as unknown as Review) : null;
}

export async function listAllReviewsLive(): Promise<Review[]> {
  try {
    const { generateClient } = await import('aws-amplify/data');
    const client = generateClient() as AnyDataClient;
    const res = await client.models.Review.list();
    return ((res.data ?? []) as unknown as Review[]).sort((a, b) =>
      b.createdAt.localeCompare(a.createdAt),
    );
  } catch (e) {
    console.error('[ShopGND] listAllReviewsLive failed', e);
    return [];
  }
}

export async function deleteReviewLive(reviewId: string): Promise<boolean> {
  try {
    const { generateClient } = await import('aws-amplify/data');
    const client = generateClient() as AnyDataClient;
    await client.models.Review.delete({ id: reviewId });
    return true;
  } catch (e) {
    console.error('[ShopGND] deleteReviewLive failed', e);
    return false;
  }
}
